import React from 'react';
import {
  StarIcon,
  HeartIcon,
  EyeIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { formatCompactCount } from '../utils/engagementMeta';

export interface BuildEngagementBarProps {
  favoriteCount?: number | null;
  likeCount?: number | null;
  viewCount?: number | null;
  /** Optional history / milestones / awards count (calendar icon). */
  milestoneCount?: number | null;
  /** Extra tooltip for the views metric (e.g. owner explanation). */
  viewTitle?: string | null;
  className?: string;
}

/**
 * Quiet glass metrics strip on the build page — star = favorites, heart = likes,
 * eye = views, calendar = milestones when provided. Display only; actions stay separate.
 */
export const BuildEngagementBar: React.FC<BuildEngagementBarProps> = ({
  favoriteCount = 0,
  likeCount = 0,
  viewCount = 0,
  milestoneCount = null,
  viewTitle = null,
  className = '',
}) => {
  const favorites = Math.max(0, Math.floor(Number(favoriteCount) || 0));
  const likes = Math.max(0, Math.floor(Number(likeCount) || 0));
  const views = Math.max(0, Math.floor(Number(viewCount) || 0));
  const milestones =
    milestoneCount === null || milestoneCount === undefined
      ? null
      : Math.max(0, Math.floor(Number(milestoneCount) || 0));

  const items: Array<{
    key: string;
    Icon: typeof StarIcon;
    value: number;
    label: string;
    title: string;
  }> = [
    {
      key: 'favorites',
      Icon: StarIcon,
      value: favorites,
      label: `${favorites.toLocaleString()} ${favorites === 1 ? 'favorite' : 'favorites'}`,
      title: `${favorites.toLocaleString()} ${favorites === 1 ? 'favorite' : 'favorites'}`,
    },
    {
      key: 'likes',
      Icon: HeartIcon,
      value: likes,
      label: `${likes.toLocaleString()} ${likes === 1 ? 'like' : 'likes'}`,
      title: `${likes.toLocaleString()} ${likes === 1 ? 'like' : 'likes'}`,
    },
    {
      key: 'views',
      Icon: EyeIcon,
      value: views,
      label: `${views.toLocaleString()} ${views === 1 ? 'view' : 'views'}`,
      title:
        viewTitle ||
        `${views.toLocaleString()} ${views === 1 ? 'view' : 'views'}`,
    },
  ];

  if (milestones !== null) {
    items.push({
      key: 'milestones',
      Icon: CalendarDaysIcon,
      value: milestones,
      label: `${milestones.toLocaleString()} ${milestones === 1 ? 'milestone' : 'milestones'}`,
      title: `${milestones.toLocaleString()} ${milestones === 1 ? 'milestone' : 'milestones'}`,
    });
  }

  return (
    <div
      className={`build-engagement-bar inline-flex items-center gap-4 sm:gap-5 px-3.5 py-2 rounded-xl border border-white/[0.08] bg-[#0c0c0c]/80 backdrop-blur-md text-gray-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] ${className}`.trim()}
      role="group"
      aria-label="Build engagement"
    >
      {items.map(({ key, Icon, value, label, title }) => (
        <span key={key} className="inline-flex items-center gap-1.5 tabular-nums text-sm" title={title}>
          <Icon className="w-4 h-4 text-gray-300 shrink-0" aria-hidden />
          <span className="sr-only">{label}</span>
          <span aria-hidden>{formatCompactCount(value)}</span>
        </span>
      ))}
    </div>
  );
};

export default BuildEngagementBar;
